import Dialogo from '@/components/Dialogo';
import { estadoPorId, podeConfirmar } from '@/agenda/confirmacao';
import { formatarTelefone } from '@/lib/telefone';

/**
 * O detalhe de um agendamento, aberto pela caixinha da grade (VF-3).
 *
 * ★ AUTO-SUFICIENTE, E ISSO É OBRIGATÓRIO. A recepção NÃO lê `leads` (as rules
 * reservam a coleção a `podeVendas`), e esta tela é dela também. Por isso o
 * painel mostra apenas o que o próprio documento do agendamento denormaliza —
 * nome, telefone, procedimento, profissional, tipo. Buscar o lead aqui daria
 * `permission-denied` para metade de quem abre a agenda.
 *
 * O botão que leva à ficha do lead só aparece para quem pode lê-la.
 *
 * ★★ WI (29/set/2026) — TODO BOTÃO QUE ESCREVE NO LEAD VIVE ATRÁS DE `podeVendas`.
 *
 * "Compareceu", "Faltou" e "Reagendar" nasceram SEM guarda, e os três escrevem no lead
 * (etapa, carimbos, interação, e no comparecimento também a tarefa de retorno). A
 * recepção via os três e levava `permission-denied` — a pessoa que menos pode resolver
 * isso. Medido em `docs/wi-medicao.md` §0: das 38 baixas que uma pessoa apertou em
 * produção, **zero** foi de recepcionista, em 2.534 agendamentos.
 *
 * ★ A peça NÃO tirou poder dela: parou de oferecer o que nunca funcionou. O que é dela
 * — ver a agenda e CONFIRMAR — ficou, e "Marcar como confirmado" NÃO pode ganhar esta
 * guarda: é escrita só do agendamento, que `podeRecepcao` já autoriza.
 *
 * Há guarda de fonte (`web/src/agenda/wi.guardas.test.js`) que descobre sozinha quais
 * funções do serviço tocam `leads` e falha se a prop que as dispara aparecer aqui fora
 * de um `podeVendas`. Ela existe para vigiar o PRÓXIMO botão, não os três de hoje.
 */
export default function DetalheAgendamento({
  caixa,
  aberto,
  aoFechar,
  podeVendas = false,
  ocupado = false,
  aoAlternar,
  aoMarcar,
  aoAbrirLead,
  aoReagendar,
  aoCancelar,
  aoAbrirConversa,
  avisoConversa = '',
  abrindoConversa = false,
}) {
  if (!caixa) return null;
  const estado = estadoPorId(caixa.estado);
  const confirmado = caixa.estado === 'confirmado';

  return (
    <Dialogo titulo={caixa.nome || 'Sem nome'} aberto={aberto} aoFechar={aoFechar}>
      <div className="dialogo-corpo">
        <p className="item-meta">
          <span className="pilula pequena" style={{ background: estado.cor, color: '#fff' }}>
            {estado.glifo} {estado.label}
          </span>
          <span>{caixa.hora}</span>
          <span>{caixa.retorno ? 'Retorno' : 'Avaliação'}</span>
          {caixa.foraDaGrade && <span>Fora da grade</span>}
        </p>

        <dl className="lista-dados">
          {caixa.telefone && (
            <>
              <dt>Telefone</dt>
              <dd>
                <a href={`tel:${caixa.telefone}`}>{formatarTelefone(caixa.telefone)}</a>
              </dd>
            </>
          )}
          {/* ★★ A-3(a) — O SDR RESPONSÁVEL. ⚠ Só aparece quando se sabe quem é:
              a recepção não carrega leads (decisão do dono), e aqui ausência é o
              caso normal, não erro. Vem do LEAD, nunca do agendamento — medido
              em 11/out: `agendamentos.sdrId` = 0 de 2.774. */}
          {caixa.sdr && (
            <>
              <dt>SDR</dt>
              {/* ★★ A-3 ajuste 4 — aqui o nome COMPLETO: o encurtamento serve à
                  coluna estreita da semana, e este diálogo tem espaço. ⚠ Mostrar
                  "Ana P." onde cabe "Ana Pereira" seria economia sem ganho. */}
              <dd>{caixa.sdrCompleto || caixa.sdr}</dd>
            </>
          )}
          {caixa.procedimento && (
            <>
              <dt>Procedimento</dt>
              <dd>{caixa.procedimento}</dd>
            </>
          )}
          {caixa.profissional && (
            <>
              <dt>Profissional</dt>
              <dd>{caixa.profissional}</dd>
            </>
          )}
        </dl>

        {podeConfirmar(caixa) && (
          <div className="dialogo-acoes-linha">
            <button
              type="button"
              className="btn-secundario"
              disabled={ocupado}
              onClick={() => aoAlternar?.(caixa, !confirmado)}
            >
              {confirmado ? 'Desmarcar confirmação' : 'Marcar como confirmado'}
            </button>
          </div>
        )}

        {/* ⚠ SÓ PARA VENDAS (WI): a baixa move a ETAPA do lead, carimba `compareceuEm`,
            escreve a timeline e, no comparecimento, cria a tarefa de retorno — tudo em
            `leads`, reservado a `podeVendas`. */}
        {podeVendas && podeConfirmar(caixa) && (
          <div className="dialogo-acoes-linha">
            <button
              type="button"
              className="btn-secundario"
              disabled={ocupado}
              onClick={() => aoMarcar?.(caixa, 'compareceu')}
            >
              Compareceu
            </button>
            <button
              type="button"
              className="btn-secundario"
              disabled={ocupado}
              onClick={() => aoMarcar?.(caixa, 'faltou')}
            >
              Faltou
            </button>
          </div>
        )}

        {/* ⚠ SÓ PARA VENDAS (WI): `reagendar` devolve o lead a `agendado`, reescreve
            `proximoAgendamento` e grava uma nota na timeline (agendamentosService.js:543).
            ★ Decisão 2 do dono: a recepção NÃO remarca — e o reagendar não foi redesenhado
            nesta peça para deixá-la remarcar sem tocar o lead. */}
        {podeVendas && podeConfirmar(caixa) && aoReagendar && (
          <div className="dialogo-acoes-linha">
            <button type="button" className="btn-secundario" disabled={ocupado}
              onClick={() => aoReagendar(caixa)}>
              Reagendar
            </button>
          </div>
        )}

        {/* ⚠ SÓ PARA VENDAS: cancelar escreve no lead (interação e, quando muda,
            a etapa), e as rules reservam `leads` a `podeVendas`. Oferecer o
            botão à recepção seria oferecer um `permission-denied`. */}
        {podeVendas && podeConfirmar(caixa) && aoCancelar && (
          <div className="dialogo-acoes-linha">
            <button type="button" className="btn-secundario perigo" disabled={ocupado}
              onClick={() => aoCancelar(caixa)}>
              Cancelar consulta
            </button>
          </div>
        )}

        {/* ★★★ A-3(b) — "ABRIR CONVERSA", pelo caminho da WV.
            ★ `guardarEstado('inbox:selecionada:<canal>')` + navegar: o
            `ConversasProvider` resgata a conversa POR ID, então agendamento
            antigo (cuja conversa está fora das 200 mais recentes do inbox)
            também abre — era exatamente o caso que travava antes da WV.

            ⚠⚠⚠ **SÓ VENDAS — DECISÃO DO DONO, 11/out/2026, e ela é PERMANENTE.**

            ★★★ *"Quem confirma avaliações é a SDR; a recepção só visualiza a agenda."*
            ⚠ Então o `podeVendas` aqui **não é limitação técnica à espera de conserto**:
            é o recorte do produto. ★ Não se implementa busca de conversa por telefone
            para a recepção, e não se afrouxa esta guarda sem decisão nova.

            ★★ O que a medição de 11/out acrescentou — e que explica por que nem o
            caminho por `leadId` existiria para ela: das **1.037** conversas do espaço
            `recepcao`, **0 têm `leadId`** (`garantirConversa` só resolve lead quando o
            espaço é `vendas`, e a resolução tardia também). ★ A decisão e o dado
            apontam para o mesmo lugar.

            ⚠ O que a recepção CONTINUA fazendo na agenda não é tocado por isto:
            **marcar e desmarcar confirmação** (o botão acima, sem guarda, e o flag da
            caixinha). ★ Guarda de fonte em `agenda/a3.guardas.test.js`. */}
        {podeVendas && caixa.leadId && aoAbrirConversa && (
          <div className="dialogo-acoes-linha">
            <button
              type="button"
              className="btn-secundario"
              disabled={ocupado || abrindoConversa}
              onClick={() => aoAbrirConversa(caixa)}
            >
              {abrindoConversa ? 'Abrindo…' : 'Abrir conversa'}
            </button>
          </div>
        )}

        {/* ★ O aviso mora DENTRO do diálogo, não no topo da página: o diálogo
            está aberto por cima, e uma mensagem lá atrás não seria lida. */}
        {avisoConversa && <p className="painel-sub">{avisoConversa}</p>}

        {podeVendas && caixa.leadId && (
          <button type="button" className="btn-texto" onClick={() => aoAbrirLead?.(caixa)}>
            Abrir ficha do lead
          </button>
        )}
      </div>
    </Dialogo>
  );
}
